import Skeleton from "@material-ui/lab/Skeleton";
import { connect, Field as FormikField, FormikErrors, getIn } from "formik";
import { get } from "lodash";
import React, { useEffect, useState } from "react";
import Description from "../Description";
import Error from "../Error";
import Input from "../Input";
import mailcheck from "mailcheck";
import EmailSuggestion from "../EmailSuggestion";
import Yup from "yup";
import { FieldType } from "@brushfire/types";

export interface FieldProps<T = any> {
	className?: string;
	fieldClassName?: string;
	component?: React.FC<T> | React.ComponentClass<T>;
	defaultValue?: string;
	description?: string | React.ReactNode;
	editInline?: boolean;
	error?: string;
	fieldRef?: React.Ref<T>;
	hiddenLabel?: boolean;
	id: string;
	inline?: boolean;
	isFieldRequired?: boolean;
	isLoading?: boolean;
	label: string | React.ReactNode | null;
	name: string;
	placeholder?: string;
	type?: string;
	value?: string;
	errorValue?: string | string[] | FormikErrors<any> | FormikErrors<any>[];
	displayIsPrimary?: boolean;
	verbiage?: string;

	[key: string]: any;
	onInteract?: (data: { id: string; focused: boolean; hovered: boolean }) => void;
	onBlur?: (e: React.FocusEvent) => void;
}

interface FieldConfirmErrors {
	value: string;
	confirmValue: string;
}

const checkIfFieldIsRequired = ({ validationSchema }: { validationSchema: Yup.ObjectSchema<any> }, name: any) => {
	const fieldValidationData = validationSchema ? (getIn as any)(validationSchema.describe().fields, name) : null;
	if (fieldValidationData) {
		const { tests: fieldTests, optional } = fieldValidationData;
		return !!fieldTests.find((t: { name: string; params: any }) => t.name === "required") || !optional;
	} else {
		return false;
	}
};

const checkEmail = (value: string, setEmailSuggestion: (suggestion: string | null) => void) => {
	mailcheck.defaultTopLevelDomains.push("org.au"); // whitelist email domains
	mailcheck.run({
		email: value,
		suggested: (suggestion: MailcheckModule.ISuggestion) => {
			setEmailSuggestion(suggestion.full);
		},
		empty: () => {
			setEmailSuggestion(null);
		},
	});
};

const stripSmartQuotes = (value: string, name: string, formik: any) => {
	const singleSmartQuotes = new RegExp(/[\u2018\u2019]/g);
	const doubleSmartQuotes = new RegExp(/[\u201C\u201D]/g);
	if (singleSmartQuotes.test(value) || doubleSmartQuotes.test(value)) {
		formik.setFieldValue(name, value.replace(/[\u2018\u2019]/g, "'").replace(/[\u201C\u201D]/g, '"'));
	}
};

const InlineField = React.forwardRef<HTMLDivElement, FieldProps>((props, ref) => {
	const {
		className,
		fieldClassName,
		component = Input.Checkbox,
		description,
		hiddenLabel,
		hasError,
		fieldRef,
		id,
		isFieldRequired,
		isLoading,
		label,
		name,
		onInteract,
		errorValue,
		formik,
		onBlur,
		...otherProps
	} = props;
	const [focused, setFocused] = useState(false);
	const [hovered, setHovered] = useState(false);
	useEffect(() => {
		if (onInteract) {
			onInteract({ id, focused, hovered });
		}
	}, [focused, hovered]);
	return (
		<div
			style={{ display: "flex", flexDirection: "column", justifyContent: "flex-start", height: "23px" }}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
		>
			<FormikField
				component={component}
				id={id}
				name={name}
				label={label}
				className={`${hasError ? "error-input" : ""} ${fieldClassName ?? ""}`}
				{...otherProps}
				innderRef={fieldRef || ref}
				onFocus={() => setFocused(true)}
				onBlur={(e: React.FocusEvent) => {
					stripSmartQuotes((e.target as HTMLInputElement).value, name, formik);
					setFocused(false);
					formik?.handleBlur(e);
					if (onBlur) onBlur(e);
				}}
			/>
			{hasError && <Error>{errorValue}</Error>}
		</div>
	);
});

const StackedField = React.forwardRef<HTMLDivElement, FieldProps>((props, ref) => {
	const {
		className,
		fieldClassName,
		component = Input.Text,
		description,
		editInline,
		hiddenLabel,
		hasError,
		fieldRef,
		id,
		isFieldRequired,
		isLoading,
		label,
		name,
		onInteract,
		errorValue,
		formik,
		onBlur,
		type,
		displayIsPrimary,
		verbiage,
		...otherProps
	} = props;

	const [focused, setFocused] = useState(false);
	const [hovered, setHovered] = useState(false);
	const [emailSuggestion, setEmailSuggestion] = useState<string | null>(null);

	const confirmErrors = get(formik.errors, props.name) || props.errorValue;

	useEffect(() => {
		if (onInteract) {
			onInteract({ id, focused, hovered });
		}
	}, [focused, hovered]);

	return (
		<div
			className={`${className || "field"}${editInline ? " edit-inline" : ""}${
				description && errorValue ? " split-error" : ""
			}`}
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
		>
			<>
				{hiddenLabel ? (
					<div className="sr-only">
						<label htmlFor={id}>
							{label} {!isFieldRequired && <span className="optional">- Optional</span>}
						</label>
					</div>
				) : (
					<label htmlFor={id}>
						{label} {!isFieldRequired && <span className="optional">- Optional</span>}
					</label>
				)}
				{isLoading ? (
					<Skeleton variant="rect" width="100%" />
				) : (
					<>
						<FormikField
							component={component}
							id={id}
							name={name}
							label={label}
							className={`${hasError ? "error-input" : ""} ${
								description ? "mb-0" : ""
							} ${fieldClassName ?? ""}`}
							innerRef={fieldRef || ref}
							onFocus={() => {
								setFocused(true);
							}}
							type={type}
							onBlur={(e: React.FocusEvent) => {
								if (type === "email") {
									checkEmail((e.target as HTMLInputElement).value, setEmailSuggestion);
								}
								stripSmartQuotes((e.target as HTMLInputElement).value, name, formik);
								setFocused(false);
								formik.handleBlur(e);
								if (onBlur) onBlur(e);
								formik.setFieldTouched(name);
							}}
							{...otherProps}
						/>
						{displayIsPrimary && (
							<span className="mini text-muted">
								<i className="fal fa-info-circle"></i> This is the primary email address associated with
								this {verbiage}
							</span>
						)}
						{description && <Description>{description}</Description>}
						{hasError && <Error>{errorValue}</Error>}
						{type === "email" && emailSuggestion && (
							<EmailSuggestion
								emailSuggestion={emailSuggestion}
								setEmailSuggestion={setEmailSuggestion}
								name={name}
								formik={formik}
							/>
						)}
					</>
				)}
			</>
		</div>
	);
});

const Field = React.forwardRef<HTMLDivElement, FieldProps>(
	({ inline = false, formik, isFieldRequired, confirmation, ...props }, ref) => {
		if (props.component?.displayName === "CheckboxInput") {
			inline = true;
		}
		if (isFieldRequired == undefined || isFieldRequired == null) {
			isFieldRequired = checkIfFieldIsRequired(formik, props.name);
		}

		const errorValue = get(formik.errors, props.name) || props.errorValue;
		const touchedValue = get(formik.touched, props.name);

		const fieldHasError = (errorValue && formik.submitCount > 0) || props.hasError;

		return inline ? (
			<InlineField
				isFieldRequired={isFieldRequired}
				{...props}
				hasError={fieldHasError}
				confirmation={confirmation}
				errorValue={errorValue}
			/>
		) : (
			<StackedField
				isFieldRequired={isFieldRequired}
				{...props}
				hasError={fieldHasError}
				confirmation={confirmation}
				errorValue={errorValue}
				formik={formik}
			/>
		);
	}
);

InlineField.displayName = "InlineField";
StackedField.displayName = "StackedField";
Field.displayName = "Field";

export default connect(Field) as React.FC<FieldProps>;
